import { useEffect, useMemo, useState } from 'react'

import { getExplicitDateRange } from '@/utils/analytics'

const utcDate = (date: Date) => date.toISOString().split('T')[0]

/**
 * getExplicitDateRange for the current UTC day. Periods end on the UTC date, which rolls over
 * mid-afternoon in the Americas, so a page left open has to move its range with it. A timer
 * alone is not enough: it fires late after the machine sleeps, so focus re-checks too.
 */
export function useExplicitDateRange(backPeriod: number, createdAt?: string) {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const schedule = (from: Date) => {
      clearTimeout(timer)
      const nextUtcMidnight = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 1)
      timer = setTimeout(refresh, nextUtcMidnight - from.getTime())
    }
    // A timer can fire just before the wall clock reaches midnight, so a same-day check reschedules.
    const refresh = () => {
      const current = new Date()
      if (utcDate(current) !== utcDate(now)) setNow(current)
      else schedule(current)
    }
    schedule(now)
    document.addEventListener('visibilitychange', refresh)
    window.addEventListener('focus', refresh)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [now])

  return useMemo(() => getExplicitDateRange(backPeriod, createdAt, now), [backPeriod, createdAt, now])
}
